import { useState, useEffect } from 'react';
import { useStateValue } from 'state';
import { CAPS_CONFIG, loadImsToken } from '../queries';
import { error as showError } from "@react/react-spectrum/Toast";

/**
 * Global CAPS integration state (persists across component remounts)
 * - cachedToken: IMS authentication token
 */
let cachedToken = null;
let cachedEmail = null;
let sessionError = false;
let needsImsLogin = false;
let tokenPromise = null;
const INTERNAL_USER_ROLE = 'internalUser';

const useCaps = () => {
  const [{ dashboard }] = useStateValue();
  const [token, setToken] = useState(cachedToken);
  const [adobeEmail, setAdobeEmail] = useState(cachedEmail);
  const [imsLoginRequired, setImsLoginRequired] = useState(needsImsLogin);

  // Get project and environment from dashboard
  const getProjectAndEnvironment = () => {
    if (!dashboard?.client || !dashboard.client.hash) {
      return { project: '', environment: '' };
    }

    const { hash: project, environment } = dashboard.client;
    return {
      project: project.split('_')[0],
      environment: environment ? environment.charAt(0).toLowerCase() + environment.slice(1) : ''
    };
  };

  const { project, environment } = getProjectAndEnvironment();
  const isInternalUser = dashboard?.user?.roles?.includes(INTERNAL_USER_ROLE) || false;
  const [isLoading, setIsLoading] = useState(!cachedToken && !needsImsLogin);
  const [capsError, setCapsError] = useState(false);

  const ReloadPage = () => {
    window.location.reload();
  };

  const fetchToken = async () => {
    try {
      const response = await loadImsToken();
      cachedToken = response.data.token;
      cachedEmail = response.data.email ?? null;
      setToken(cachedToken);
      setAdobeEmail(cachedEmail);
      needsImsLogin = false;
      setImsLoginRequired(false);
      setCapsError(false);
      setIsLoading(false);
      return response;
    } catch (error) {
      if (error.response?.status === 401) {
        const errorCode = error.response?.data?.code;

        // Check if backend indicates IMS session is required
        if (errorCode === 'IMS_SESSION_REQUIRED') {
          needsImsLogin = true;
          cachedEmail = null;
          setImsLoginRequired(true);
          setAdobeEmail(null);
          setIsLoading(false);
          throw error;
        }

        // Other 401 errors - show original error toast
        cachedEmail = null;
        setAdobeEmail(null);
        if (!sessionError) {
          showError(`We need to refresh your session. Please log in again to continue.`, {
            timeout: 0,
            actionLabel: 'Reload Page',
            onAction: ReloadPage,
            shouldCloseOnAction: true
          });
          sessionError = true;
        }
      } else {
        setCapsError(true);
      }
      setIsLoading(false);
      throw error;
    }
  };

  // Only fetch token if we don't have it cached
  useEffect(() => {
    let isSubscribed = true;

    const initializeToken = async () => {
      // If token is already cached, use it immediately
      if (cachedToken) {
        if (isSubscribed) {
          setToken(cachedToken);
          setIsLoading(false);
        }
        return;
      }

      // If a fetch is already in progress, reuse the same promise
      if (!tokenPromise) {
        tokenPromise = fetchToken()
          .then(() => {
            // After successful fetch, clear the promise
            tokenPromise = null;
          })
          .catch((error) => {
            // On error, clear the promise so it can be retried
            tokenPromise = null;
            throw error;
          });
      }

      // Wait for the promise (whether we created it or reused it).
      // Capture in a local variable so clearing tokenPromise in .then()/.catch()
      // does not affect this await.
      const promise = tokenPromise;
      await promise;

      // Update local state if still mounted
      if (cachedToken && isSubscribed) {
        setToken(cachedToken);
      }
    };

    initializeToken()
      .catch(console.error);

    return () => isSubscribed = false;
  }, []);

  return {
    capsError,
    config: {
      endpoint: CAPS_CONFIG.API_URL,
      token: token
    },
    project,
    environment,
    capsUiUrl: CAPS_CONFIG.UI_URL,
    isLoading,
    isInternalUser,
    fetchToken,
    imsLoginRequired,
    adobeEmail
  };
};

export default useCaps;
